import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import callApi from "helpers/callApi";
import { parseServerError } from "utils/errors";
import { formatDateForServer } from "utils/dates";
import {
  applyEmptyPeriodicityFallback,
  buildTaxSettingPeriodicityQuery,
  normalizeReportPeriodicityResponse,
  normalizeTaxSettingPeriodicityResponse,
} from "utils/taxSettingPeriodicity";

export const importInventoryFile = createAsyncThunk(
    "inventory/importInventoryFile",
    async (file, { rejectWithValue }) => {
      try {
        const response = await callApi("/inventory/import", "POST", file, {
          contentType: "multipart/form-data",
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          return rejectWithValue({
            success: false,
            errors: errorData,
            message: 'Validation errors in uploaded data'
          });
        }
        
        const data = await response.json();
        
        // Check if there are validation errors in successful response
        if (data.errors && data.errors.length > 0) {
          return rejectWithValue({
            success: false,
            errors: data.errors,
            message: 'Validation errors in uploaded data',
            updated: data.updated,
            created: data.created
          });
        }
        
        return data;
      } catch (err) {
        return rejectWithValue({
          success: false,
          message: err.message || 'Server error'
        });
      }
    }
);

export const inventoryRecalculate = createAsyncThunk(
    "inventory/inventoryRecalculate",
    async (data, { rejectWithValue }) => {
      try {
        const response = await callApi("/inventory/recalculate", "POST", data);
        if (response.ok) {
          const json = await response.json();
          return json;
        } else {
          const error = await response.json();
          return rejectWithValue(error);
        }
      } catch (err) {
        return rejectWithValue("Server error");
      }
    }
);

export const inventoryEdit = createAsyncThunk(
    "inventory/inventoryEdit",
    async (data, { rejectWithValue }) => {
      try {
        const response = await callApi("/inventory/edit", "PUT", data);
        if (response.ok) {
          const json = await response.json();
          return json;
        } else {
          const error = await response.json();
          return rejectWithValue(error);
        }
      } catch (err) {
        return rejectWithValue("Server error");
      }
    }
);

export const fetchGbrStates = createAsyncThunk(
  "states/fetchGbrStates",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi("/states?country_id=GBR", "GET");
      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        return rejectWithValue(response.status);
      }
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const fetchInventory = createAsyncThunk(
  "states/fetchInventory",
  async (params = {}, { rejectWithValue }) => {
    try {
      const { offset = 0, limit = 25, sku_or_description } = params;
      const queryParams = new URLSearchParams({ offset: offset.toString(), limit: limit.toString() });
      if (sku_or_description) {
        queryParams.append('sku_or_description', sku_or_description);
      }
      const response = await callApi(`/inventory?${queryParams}`, "GET");
      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        return rejectWithValue(response.status);
      }
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const saveAutosubmit = createAsyncThunk(
  "autosubmit/save",
  async (data, { rejectWithValue }) => {
    try {
      const response = await callApi("/autosubmit/save", "POST", data);
      if (response.ok) {
        const json = await response.json();
        return json;
      } else {
        const error = await response.json();
        return rejectWithValue(error);
      }
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const fetchAutosubmitStores = createAsyncThunk(
  "autosubmit/fetchStores",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi("/autosubmit/stores", "GET");
      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        return rejectWithValue(response.status);
      }
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const fetchAutosubmitCountries = createAsyncThunk(
  "autosubmit/fetchCountries",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi("/autosubmit/countries", "GET");
      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        return rejectWithValue(response.status);
      }
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const fetchStates = createAsyncThunk(
  "states/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi("/us_states", "GET");
      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        return rejectWithValue(response.status);
      }
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const fetchExTypes = createAsyncThunk(
  "exemptionTypes/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi(
        "/exemption/exemption_document_type",
        "GET"
      );
      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        return rejectWithValue(response.status);
      }
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

function normalizeCertTypesPayload(data) {
  const raw = Array.isArray(data)
    ? data
    : data?.data ?? data?.certificate_types ?? data?.items ?? [];
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .map((row) => {
      if (!row || typeof row !== "object") {
        return null;
      }
      const id =
        row.id ?? row.certificate_type_id ?? row.type_id ?? row.value;
      const name = row.name ?? row.title ?? row.label ?? "";
      if (id === undefined || id === null || id === "") {
        return null;
      }
      const numId = typeof id === "number" ? id : parseInt(String(id), 10);
      if (Number.isNaN(numId)) {
        return null;
      }
      return { id: numId, name: String(name) };
    })
    .filter(Boolean);
}

export const fetchCertTypes = createAsyncThunk(
  "certificateTypes/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi(
        "/sales_tax_permit/certificate_types",
        "GET"
      );
      if (response.ok) {
        const data = await response.json();
        return normalizeCertTypesPayload(data);
      } else {
        return rejectWithValue(response.status);
      }
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const deleteWaiver = createAsyncThunk(
  "waiver/delete",
  async (id, { rejectWithValue }) => {
    try {
      const response = await callApi(`/waiver/delete/${id}`, "DELETE");
      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        return rejectWithValue(response.status);
      }
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const downloadWaiverFile = createAsyncThunk(
  "waiver/downloadFile",
  async (id, { rejectWithValue }) => {
    try {
      const response = await callApi(`/waiver/download/${id}`, "GET", {
        responseType: "blob",
      });
      if (response.ok) {
        const blob = await response.json();
        return { link: window.URL.createObjectURL(blob) };
      } else {
        return rejectWithValue(response.status);
      }
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const deleteSalesTaxPermit = createAsyncThunk(
  "salesTaxPermit/delete",
  async (id, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/sales_tax_permit/delete/${id}`,
        "DELETE"
      );
      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        return rejectWithValue(response.status);
      }
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const deleteExeption = createAsyncThunk(
  "exemption/delete",
  async (id, { rejectWithValue }) => {
    try {
      const response = await callApi(`/exemption/delete/${id}`, "DELETE");
      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        return rejectWithValue(response.status);
      }
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const downloadExemptionFile = createAsyncThunk(
  "exemption/downloadFile",
  async (id, { rejectWithValue }) => {
    try {
      const response = await callApi(`/exemption/download/${id}`, "GET", {
        responseType: "blob",
      });
      if (response.ok) {
        const blob = await response.json();
        return { link: window.URL.createObjectURL(blob) };
      } else {
        return rejectWithValue(response.status);
      }
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const fetchExemptions = createAsyncThunk(
  "exemptions/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi("/exemption", "GET");
      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        return rejectWithValue(response.status);
      }
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const fetchSalesTaxPermit = createAsyncThunk(
  "salesTaxPermit/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi("/sales_tax_permit", "GET");
      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        return rejectWithValue(response.status);
      }
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

/**
 * GET /countries/tax_setting_periodicity/ — reporting periodicity for VAT / Sales tax / EPR.
 */
export const fetchTaxSettingPeriodicity = createAsyncThunk(
  "taxSettings/fetchTaxSettingPeriodicity",
  async (query = {}, { rejectWithValue }) => {
    try {
      const payload = buildTaxSettingPeriodicityQuery(query);
      const response = await callApi(
        "/countries/tax_setting_periodicity/",
        "GET",
        payload
      );
      if (!response.ok) {
        return rejectWithValue(response.status);
      }
      const data = await response.json();
      const normalized = normalizeTaxSettingPeriodicityResponse(data);
      return applyEmptyPeriodicityFallback(normalized);
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

/**
 * GET /countries/report_periodicity/ — period options for Create VAT return (VAT: country + reason only).
 */
export const fetchReportPeriodicity = createAsyncThunk(
  "taxSettings/fetchReportPeriodicity",
  async (query = {}, { rejectWithValue }) => {
    try {
      const payload = buildTaxSettingPeriodicityQuery(query);
      const response = await callApi(
        "/countries/report_periodicity/",
        "GET",
        payload
      );
      if (!response.ok) {
        return rejectWithValue(response.status);
      }
      const data = await response.json();
      const normalized = normalizeReportPeriodicityResponse(data);
      return applyEmptyPeriodicityFallback(normalized);
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const createWaiver = createAsyncThunk(
  "waiver/create",
  async (
    { waiverCert, waiverState, waiverSelected, data },
    { rejectWithValue }
  ) => {
    try {
      const response = await callApi(
        `/waiver/add?certificate_number=${waiverCert}&from_date=${formatDateForServer(
          waiverSelected.start
        )}${
          waiverSelected.end
            ? `&till_date=${formatDateForServer(waiverSelected.end)}`
            : ""
        }&state_code=${waiverState}`,
        "POST",
        { data }
      );
      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        return rejectWithValue(response.status);
      }
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const fetchWaivers = createAsyncThunk(
  "waivers/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi("/waiver", "GET");
      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        return rejectWithValue(response.status);
      }
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const createSalesTaxPermit = createAsyncThunk(
  "salesTaxPermit/create",
  async (data, { rejectWithValue }) => {
    try {
      const response = await callApi("/sales_tax_permit/add", "POST", data);
      if (response.ok) {
        const responseData = await response.json();
        return { status: response.status, data: responseData };
      } else {
        // Try to parse error response body
        let errorData = null;
        try {
          const responseText = await response.text();
          if (responseText) {
            const parsed = JSON.parse(responseText);
            // Check if parsed data is an array (validation errors format)
            // Server returns: [{type: "int_type", loc: ["body", "data", "type"], msg: "Input should be a valid integer"}]
            if (Array.isArray(parsed)) {
              errorData = parsed;
            } else if (parsed.detail && Array.isArray(parsed.detail)) {
              // FastAPI format with detail array
              errorData = parsed.detail;
            } else {
              // Other formats - wrap in object to maintain structure
              errorData = parsed;
            }
          }
        } catch (e) {
          // If parsing fails, use null
          errorData = null;
        }
        return rejectWithValue({ 
          status: response.status, 
          error: errorData 
        });
      }
    } catch (err) {
      return rejectWithValue({ 
        status: 500, 
        error: "Server error" 
      });
    }
  }
);

export const createExeption = createAsyncThunk(
  "exemption/create",
  async (
    { exempCert, exempState, exempSelected, data },
    { rejectWithValue }
  ) => {
    try {
      const response = await callApi(
        `/exemption/add?tax_exemption_certificate=${exempCert}&from_date=${formatDateForServer(
          exempSelected.start
        )}${
          exempSelected.end
            ? `&till_date=${formatDateForServer(exempSelected.end)}`
            : ""
        }&state_code=${exempState}`,
        "POST",
        { data }
      );
      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        return rejectWithValue(response.status);
      }
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

const initialState = {
  salesTaxPermit: [],
  certTypes: [],
  usStates: [],
  exemptions: [],
  exTypes: [],
  waivers: [],
  autoCountries: [],
  autoStores: [],
  inventories: []
};

const taxSettingsSlice = createSlice({
  name: "taxSettings",
  initialState,
  reducers: {
    updateTaxSettingsAutopilotCountriesList: (state, action) => {
      const updateData = action.payload;
      const dataArray = JSON.parse(JSON.stringify(state.autoCountries));
      
      updateData.countries.forEach(updateItem => {
        dataArray.forEach(item => {
          if (
            item.country.code === updateItem.country_code &&
            item.type_of_report.id === updateItem.type_of_report_id
          ) {
            item.type_of_report.autosubmit = updateItem.autosubmit;
          }
        });
      });
      state.autoCountries = dataArray;
    },
    clearAutosubmitSaveFeedback: (state) => {
      state.savingAutoError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(importInventoryFile.pending, (state) => {
        state.fetchingImportInventoryFile = true;
      })
      .addCase(importInventoryFile.fulfilled, (state, action) => {
        state.fetchingImportInventoryFile = false;
        state.fetchingImportInventoryFileError = null;
      })
      .addCase(importInventoryFile.rejected, (state, action) => {
        state.fetchingImportInventoryFile = false;
        state.fetchingImportInventoryFileError = parseServerError(action.error);
      })
      .addCase(inventoryRecalculate.pending, (state) => {
        state.fetchingInventoryRecalculate = true;
      })
      .addCase(inventoryRecalculate.fulfilled, (state, action) => {
        state.fetchingInventoryRecalculate = false;
        state.inventoryRecalculate = action.payload;
        state.fetchingInventoryRecalculateError = null;
      })
      .addCase(inventoryRecalculate.rejected, (state, action) => {
        state.fetchingInventoryRecalculate = false;
        state.fetchingInventoryRecalculateError = parseServerError(action.error);
      })
      .addCase(inventoryEdit.pending, (state) => {
        state.fetchingInventoryEdit = true;
      })
      .addCase(inventoryEdit.fulfilled, (state, action) => {
        state.fetchingInventoryEdit = false;
        state.inventoryEdit = action.payload;
        state.fetchingInventoryEditError = null;
      })
      .addCase(inventoryEdit.rejected, (state, action) => {
        state.fetchingInventoryEdit = false;
        state.fetchingInventoryEditError = parseServerError(action.error);
      })
      .addCase(fetchGbrStates.pending, (state) => {
        state.fetchingGbrStates = true;
      })
      .addCase(fetchGbrStates.fulfilled, (state, action) => {
        state.fetchingGbrStates = false;
        state.gbrStates = action.payload;
        state.fetchingGbrStatesError = null;
      })
      .addCase(fetchGbrStates.rejected, (state, action) => {
        state.fetchingGbrStates = false;
        state.fetchingGbrStatesError = parseServerError(action.error);
      })
      .addCase(fetchInventory.pending, (state) => {
        state.fetchingInventory = true;
      })
      .addCase(fetchInventory.fulfilled, (state, action) => {
        state.fetchingInventory = false;
        state.inventories = action.payload;
        state.fetchingInventoryError = null;
      })
      .addCase(fetchInventory.rejected, (state, action) => {
        state.fetchingInventory = false;
        state.fetchingInventoryError = parseServerError(action.error);
      })
      .addCase(saveAutosubmit.pending, (state) => {
        state.savingAuto = true;
        state.savingAutoError = null;
      })
      .addCase(saveAutosubmit.fulfilled, (state) => {
          state.savingAuto = false;
          state.savingAutoError = null;
      })
      .addCase(saveAutosubmit.rejected, (state, action) => {
        state.savingAuto = false;
        // rejectWithValue puts API body in payload; action.error.message is just "Rejected"
        state.savingAutoError =
          action.payload !== undefined
            ? parseServerError(action.payload)
            : parseServerError(action.error);
      })
      .addCase(fetchAutosubmitStores.pending, (state) => {
        state.fetchingAutoStores = true;
      })
      .addCase(fetchAutosubmitStores.fulfilled, (state, action) => {
        state.autoStores = action.payload.stores;
        state.fetchingAutoStores = false;
        state.fetchingAutoStoresError = null;
      })
      .addCase(fetchAutosubmitStores.rejected, (state, action) => {
        state.fetchingAutoStores = false;
        state.fetchingAutoStoresError = parseServerError(action.error);
      })
      .addCase(fetchAutosubmitCountries.pending, (state) => {
        state.fetchingAutoCountries = true;
      })
      .addCase(fetchAutosubmitCountries.fulfilled, (state, action) => {
        state.autoCountries = action.payload;
        state.fetchingAutoCountries = false;
        state.fetchingAutoCountriesError = null;
      })
      .addCase(fetchAutosubmitCountries.rejected, (state, action) => {
        state.fetchingAutoCountries = false;
        state.fetchingAutoCountriesError = parseServerError(action.error);
      })
      .addCase(fetchStates.pending, (state) => {
        state.fetchingStates = true;
      })
      .addCase(fetchStates.fulfilled, (state, action) => {
        state.usStates = action.payload;
        state.fetchingStates = false;
        state.fetchingStatesError = null;
      })
      .addCase(fetchStates.rejected, (state, action) => {
        state.fetchingStates = false;
        state.fetchingStatesError = parseServerError(action.error);
      })
      .addCase(fetchExTypes.pending, (state) => {
        state.fetchingExTypes = true;
      })
      .addCase(fetchExTypes.fulfilled, (state, action) => {
        state.exTypes = action.payload;
        state.fetchingExTypes = false;
        state.fetchingExTypesError = null;
      })
      .addCase(fetchExTypes.rejected, (state, action) => {
        state.fetchingExTypes = false;
        state.fetchingExTypesError = parseServerError(action.error);
      })
      .addCase(fetchCertTypes.pending, (state) => {
        state.fetchingCertTypes = true;
      })
      .addCase(fetchCertTypes.fulfilled, (state, action) => {
        state.certTypes = action.payload;
        state.fetchingCertTypes = false;
        state.fetchingCertTypesError = null;
      })
      .addCase(fetchCertTypes.rejected, (state, action) => {
        state.fetchingCertTypes = false;
        state.fetchingCertTypesError = parseServerError(action.error);
      })
      .addCase(deleteWaiver.pending, (state) => {
        state.deletingWaiver = true;
        state.deletingWaiverError = null;
      })
      .addCase(deleteWaiver.fulfilled, (state) => {
        state.deletingWaiver = false;
        state.deletingWaiverError = null;
      })
      .addCase(deleteWaiver.rejected, (state, action) => {
        state.deletingWaiver = false;
        state.deletingWaiverError = parseServerError(action.error);
      })
      .addCase(downloadWaiverFile.pending, (state) => {
        state.downloadingWaiver = true;
      })
      .addCase(downloadWaiverFile.fulfilled, (state, action) => {
        state.downloadingWaiver = false;
        state.downloadingWaiverError = null;
        state.toDownloadWaiver = { ...action.payload };
      })
      .addCase(downloadWaiverFile.rejected, (state, action) => {
        state.downloadingWaiver = false;
        state.downloadingWaiverError = parseServerError(action.error);
      })
      .addCase(deleteSalesTaxPermit.pending, (state) => {
        state.deletingTaxPermit = true;
        state.deletingTaxPermitError = null;
      })
      .addCase(deleteSalesTaxPermit.fulfilled, (state) => {
        state.deletingTaxPermit = false;
        state.deletingTaxPermitError = null;
      })
      .addCase(deleteSalesTaxPermit.rejected, (state, action) => {
        state.deletingTaxPermit = false;
        state.deletingTaxPermitError = parseServerError(action.error);
      })
      .addCase(deleteExeption.pending, (state) => {
        state.deletingExemp = true;
        state.deletingExempError = null;
      })
      .addCase(deleteExeption.fulfilled, (state) => {
        state.deletingExemp = false;
        state.deletingExempError = null;
      })
      .addCase(deleteExeption.rejected, (state, action) => {
        state.deletingExemp = false;
        state.deletingExempError = parseServerError(action.error);
      })
      .addCase(downloadExemptionFile.pending, (state) => {
        state.downloadingEx = true;
      })
      .addCase(downloadExemptionFile.fulfilled, (state, action) => {
        state.downloadingEx = false;
        state.downloadingExError = null;
        state.toDownloadEx = { ...action.payload };
      })
      .addCase(downloadExemptionFile.rejected, (state, action) => {
        state.downloadingEx = false;
        state.downloadingExError = parseServerError(action.error);
      })
      .addCase(fetchExemptions.pending, (state) => {
        state.fetchingEx = true;
      })
      .addCase(fetchExemptions.fulfilled, (state, action) => {
        state.exemptions = action.payload;
        state.fetchingEx = false;
        state.fetchingExError = null;
      })
      .addCase(fetchExemptions.rejected, (state, action) => {
        state.fetchingEx = false;
        state.fetchingExError = parseServerError(action.error);
      })
      .addCase(fetchSalesTaxPermit.pending, (state) => {
        state.fetchingTaxPermit = true;
      })
      .addCase(fetchSalesTaxPermit.fulfilled, (state, action) => {
        state.salesTaxPermit = action.payload;
        state.fetchingTaxPermit = false;
        state.fetchingTaxPermitError = null;
      })
      .addCase(fetchSalesTaxPermit.rejected, (state, action) => {
        state.fetchingTaxPermit = false;
        state.fetchingTaxPermitError = parseServerError(action.error);
      })
      .addCase(createWaiver.pending, (state) => {
        state.creatingWaivers = true;
      })
      .addCase(createWaiver.fulfilled, (state, action) => {
        state.creatingWaivers = false;
        state.creatingWaiversError = null;
        state.createdWaiversId = action.payload.id;
      })
      .addCase(createWaiver.rejected, (state, action) => {
        state.creatingWaivers = false;
        state.creatingWaiversError = parseServerError(action.error);
      })
      .addCase(fetchWaivers.pending, (state) => {
        state.fetchingWaivers = true;
      })
      .addCase(fetchWaivers.fulfilled, (state, action) => {
        state.waivers = action.payload;
        state.fetchingWaivers = false;
        state.fetchingWaiversError = null;
      })
      .addCase(fetchWaivers.rejected, (state, action) => {
        state.fetchingWaivers = false;
        state.fetchingWaiversError = parseServerError(action.error);
      })
      .addCase(createSalesTaxPermit.pending, (state) => {
        state.creatingTaxPermit = true;
      })
      .addCase(createSalesTaxPermit.fulfilled, (state, action) => {
        state.creatingTaxPermit = false;
        state.creatingTaxPermitError = null;
        state.createdTaxPermitId = action.payload.id;
      })
      .addCase(createSalesTaxPermit.rejected, (state, action) => {
        state.creatingTaxPermit = false;
        state.creatingTaxPermitError = parseServerError(action.error);
      })
      .addCase(createExeption.pending, (state) => {
        state.creatingEx = true;
      })
      .addCase(createExeption.fulfilled, (state, action) => {
        state.creatingEx = false;
        state.creatingExError = null;
        state.createdExId = action.payload.id;
      })
      .addCase(createExeption.rejected, (state, action) => {
        state.creatingEx = false;
        state.creatingExError = parseServerError(action.error);
      });
  },
});

export const {
  updateTaxSettingsAutopilotCountriesList,
  clearAutosubmitSaveFeedback,
} = taxSettingsSlice.actions

export default taxSettingsSlice.reducer;
